import React from "react";
import { UserContext } from "../Definitions/UserContext";
import { NicknameLinkData } from "../Utilities/BusinessData";
import { GetAliasLink, GetPageURLFromPageId } from "../Utilities/LinkUtils";
import { NoRecordsBanner } from "../OopsBanner";
import { decodeAmp } from "../Utilities/StringUtils";

interface RenderBusinessNicknameLinkProps {
    data: NicknameLinkData | { isStenciled: boolean };
    layout: string;
    userContext: UserContext;
    displayAsLink?: boolean;
    linkPage: string;
    linkTarget: string;
    textBefore?: string;
    textAfter?: string;
    includeTextInLink?: boolean;
    nicknameLinkTemplate?: string;
}

/**
 * RenderBusinessNicknameLink - Displays the location nickname as a clickable link or plain text
 * When displayAsLink is true (default), navigates to an internal page using the duplicate page link pattern
 * When displayAsLink is false, displays as plain text
 *
 * NOTE: This component only displays data on renter/location pages (pages with a nickname).
 * In the editor, the main account doesn't have nickname data, so a placeholder link is shown
 */
export default function RenderBusinessNicknameLink({
    data,
    layout = "span",
    userContext,
    displayAsLink = true,
    linkPage,
    linkTarget,
    textBefore = "",
    textAfter = "",
    includeTextInLink = true,
    nicknameLinkTemplate = "{nickname}"
}: RenderBusinessNicknameLinkProps) {
    // Determine if in editor mode (check early for conditional messaging)
    const isEditorMode = (window as Window & { isEditor?: boolean }).isEditor === true;

    const isStenciled = (data as { isStenciled: boolean })?.isStenciled === true;

    let nicknameLinkData = data as NicknameLinkData;

    const needsPlaceholder = isStenciled || (isEditorMode && !nicknameLinkData?.LocationNickName);
    if (needsPlaceholder) {
        nicknameLinkData = {
            LocationNickName: "[Location Nickname]",
            URLSlug: "placeholder"
        };
    }

    if (!nicknameLinkData?.LocationNickName) {
        return null;
    }

    let nicknameText = nicknameLinkTemplate.replace("{nickname}", decodeAmp(nicknameLinkData.LocationNickName));

    if(textAfter){
        textAfter = textAfter.replace("{studioNumber}", userContext.studioNumber || "").replace("  "," ");
    }
    if(textBefore){
        textBefore = textBefore.replace("{studioNumber}", userContext.studioNumber || "").replace("  "," ");
    }
    nicknameText = nicknameText.replace("{studioNumber}", userContext.studioNumber || "").replace("  "," ");


    const renderTextElement = (content: React.ReactNode) => {
        const baseClassName = "w-full nickname-link-text sub-inherit-color sub-inherit-f";
        const className = baseClassName + (isStenciled ? " stenciled" : "");
        const stencilItemClass = isStenciled ? "stencil-item" : "";

        switch (layout) {
            case "h-1":
                return <h1 className={className}><span className={stencilItemClass}>{content}</span></h1>;
            case "h-2":
                return <h2 className={className}><span className={stencilItemClass}>{content}</span></h2>;
            case "h-3":
                return <h3 className={className}><span className={stencilItemClass}>{content}</span></h3>;
            case "h-4":
                return <h4 className={className}><span className={stencilItemClass}>{content}</span></h4>;
            case "p-1":
                return <p className={"vp-1 " + className}><span className={stencilItemClass}>{content}</span></p>;
            case "p-2":
                return <p className={"vp-2 " + className}><span className={stencilItemClass}>{content}</span></p>;
            case "p-3":
                return <p className={"vp-3 " + className}><span className={stencilItemClass}>{content}</span></p>;
            default:
                return <span className={className}><span className={stencilItemClass}>{content}</span></span>;
        }
    };

    // Text-only mode
    if (!displayAsLink) {
        return renderTextElement(
            <>
                {textBefore && <span>{textBefore} </span>}
                <span className="nickname-text">{nicknameText}</span>
                {textAfter && <span> {textAfter}</span>}
            </>
        );
    }

    // Link mode - need URLSlug for link construction
    if (!nicknameLinkData?.URLSlug) {
        return <NoRecordsBanner message="No nickname link available" />;
    }

    const pageURL = GetPageURLFromPageId(linkPage, userContext);

    // Construct the link URL using GetAliasLink
    const href = GetAliasLink(
        nicknameLinkData.URLSlug,
        pageURL,
        userContext,
        true // isLocationNickName - encode the alias
    );

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (isEditorMode) {
            e.preventDefault();
        }
    };

    return renderTextElement(
        <>
            {textBefore && <span>{textBefore} </span>}
            <a
                href={href}
                target={linkTarget || "_self"}
                rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
                onClick={handleClick}
                className="nickname-link"
            >
                {nicknameText}
            </a>
            {textAfter && <span> {textAfter}</span>}
        </>
    );
}
